/**
 * `geo_settings` — настройки гео-стрима инспекторов.
 *
 * Источник правды: миграция `supabase/migrations/20260510120000_geo_settings.sql`
 * в репо `stryzh`. Таблица single-row (id=1). Read через RPC `get_geo_settings`
 * (доступно всем authenticated). Write через RPC `update_geo_settings(jsonb)`
 * под `is_admin()`. Партиальное обновление: передавай только то, что меняешь.
 */
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

export type LocationAccuracy =
  | 'lowest'
  | 'low'
  | 'medium'
  | 'high'
  | 'best'
  | 'bestForNavigation'

export interface GeoSettings {
  id: number
  idle_ios_accuracy: LocationAccuracy
  idle_ios_distance_filter_m: number
  idle_android_accuracy: LocationAccuracy
  idle_android_interval_s: number
  active_ios_accuracy: LocationAccuracy
  active_ios_distance_filter_m: number
  active_android_accuracy: LocationAccuracy
  active_android_interval_s: number
  watchdog_silence_minutes: number
  /** Период принудительного keepalive ('я живой') от инспектора в active_order. */
  active_keepalive_interval_s: number
  idle_keepalive_interval_s: number
  /** Порог тишины: если keepalive не приходил > grace_s — связь считается потерянной. */
  active_silent_grace_s: number
  idle_silent_grace_s: number
  updated_at: string
  updated_by: string | null
}

/** Партиальное обновление — все поля опциональны. */
export type GeoSettingsPatch = Partial<
  Omit<GeoSettings, 'id' | 'updated_at' | 'updated_by'>
>

export async function getGeoSettings(): Promise<GeoSettings> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('get_geo_settings')
  if (error) throw new Error(error.message)
  // RPC RETURNS public.geo_settings — supabase-js отдаёт объект напрямую.
  return data as GeoSettings
}

export async function updateGeoSettings(
  patch: GeoSettingsPatch,
): Promise<GeoSettings> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('update_geo_settings', {
    p_settings: patch,
  })
  if (error) throw new Error(error.message)
  return data as GeoSettings
}

/** Дефолты — синхронизированы с DEFAULT в миграции. Используются для кнопки
 *  «Сбросить к дефолтам». */
export const GEO_SETTINGS_DEFAULTS: GeoSettingsPatch = {
  idle_ios_accuracy: 'low',
  idle_ios_distance_filter_m: 500,
  idle_android_accuracy: 'low',
  idle_android_interval_s: 300,
  active_ios_accuracy: 'medium',
  active_ios_distance_filter_m: 50,
  active_android_accuracy: 'high',
  active_android_interval_s: 15,
  watchdog_silence_minutes: 10,
  active_keepalive_interval_s: 120,
  idle_keepalive_interval_s: 300,
  active_silent_grace_s: 240,
  idle_silent_grace_s: 900,
}

export const ACCURACY_OPTIONS: ReadonlyArray<{
  value: LocationAccuracy
  label: string
  hint: string
}> = [
  { value: 'lowest', label: 'lowest', get hint() { return i18n.t('settings.accuracy.lowest') } },
  { value: 'low', label: 'low', get hint() { return i18n.t('settings.accuracy.low') } },
  { value: 'medium', label: 'medium', get hint() { return i18n.t('settings.accuracy.medium') } },
  { value: 'high', label: 'high', get hint() { return i18n.t('settings.accuracy.high') } },
  { value: 'best', label: 'best', get hint() { return i18n.t('settings.accuracy.best') } },
  {
    value: 'bestForNavigation',
    label: 'bestForNavigation',
    get hint() { return i18n.t('settings.accuracy.bestForNavigation') },
  },
]
